import React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Wrench, ClipboardCheck, LogOut, ChevronRight } from "lucide-react";
import { useAuth, canSwitchMode, homePathFor } from "@/context/AuthContext";

export default function ModeSelect() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Only mode-switchable staff (dual-role or admins) use the chooser;
  // everyone else goes to their home.
  if (user && !canSwitchMode(user)) return <Navigate to={homePathFor(user)} replace />;

  const go = (path) => navigate(path, { replace: true });
  const firstName = user?.name?.split(" ")[0] || "there";
  const onLogout = () => { logout(); navigate("/login", { replace: true }); };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50" data-testid="mode-select">
      <header className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 sm:px-8">
        <img src="/fdcheck-logo.jpg" alt="FD Check" className="h-9 w-auto object-contain" />
        <button onClick={onLogout} className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800" data-testid="mode-logout">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </header>

      <main className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-3xl">
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold text-slate-900 sm:text-4xl">Hi {firstName} 👋</h1>
            <p className="mt-2 text-slate-500">How are you working today? You can switch anytime from the menu.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <ModeCard
              testId="mode-engineer"
              onClick={() => go("/engineer")}
              icon={Wrench}
              accent="bg-[#ED1C24]"
              title="Engineer mode"
              desc="Just the jobs assigned to you — today's work, upcoming and completed."
            />
            <ModeCard
              testId="mode-inspector"
              onClick={() => go("/dashboard")}
              icon={ClipboardCheck}
              accent="bg-slate-900"
              title="Inspector mode"
              desc="Full office console — buildings, inspections, quotes, reports and more."
            />
          </div>
        </div>
      </main>
    </div>
  );
}

function ModeCard({ icon: Icon, title, desc, accent, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="group flex flex-col items-start gap-4 rounded-2xl border border-slate-200 bg-white p-6 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
    >
      <span className={`flex h-14 w-14 items-center justify-center rounded-xl ${accent} text-white`}>
        <Icon className="h-7 w-7" />
      </span>
      <div>
        <p className="flex items-center gap-1 text-lg font-bold text-slate-900">
          {title}
          <ChevronRight className="h-5 w-5 text-slate-300 transition-transform group-hover:translate-x-0.5" />
        </p>
        <p className="mt-1 text-sm text-slate-500">{desc}</p>
      </div>
    </button>
  );
}
